import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Link, Shield, Clock, Hash, CheckCircle, ArrowRight, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DECISION_COLORS = {
  model_switch: "#3b82f6",
  cost_optimization: "#10b981",
  latency_optimization: "#8b5cf6",
  provider_failover: "#f59e0b",
  quality_upgrade: "#06b6d4",
};

export default function BlockchainLedger() {
  const [blocks, setBlocks] = useState([]);
  const [stats, setStats] = useState({ total_blocks: 0, total_savings: 0, quantum_blocks: 0, verified_pct: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDecision, setFilterDecision] = useState("all");
  const [filterSolver, setFilterSolver] = useState("all");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const fetchBlocks = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterDecision !== "all") params.set("decision", filterDecision);
      if (filterSolver !== "all") params.set("solver", filterSolver);
      if (search) params.set("search", search);

      const res = await axios.get(`${API}/ledger/blocks?${params.toString()}`);
      setBlocks(res.data.blocks || []);
      setStats(res.data.stats || {});
    } catch {
      /* non-blocking */
    }
    setLoading(false);
  }, [filterDecision, filterSolver, search]);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const verifyChain = async () => {
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/ledger/verify`);
      setVerifyResult(res.data);
      if (res.data.valid) toast.success(`Chain verified! ${res.data.blocks_checked} blocks, all links intact.`);
      else toast.error(`Chain integrity broken at ${res.data.broken_links.length} link(s)`);
    } catch {
      toast.error("Verification failed");
    }
    setVerifying(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="blockchain-ledger">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--qn-foreground-muted)] mr-2" />
        <span className="text-sm text-[var(--qn-foreground-muted)]">Loading ledger blocks...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="blockchain-ledger">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-outfit font-bold text-[var(--qn-foreground)] flex items-center gap-2">
            <Link className="w-5 h-5 text-[var(--qn-primary)]" />
            Optimization Decision Ledger
            <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">Live Data</Badge>
          </h2>
          <p className="text-xs text-[var(--qn-foreground-muted)] mt-1">Immutable audit trail — SHA-256 chained blocks on Ethereum L2 (Optimism)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchBlocks} className="h-8"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
          <Button variant="outline" size="sm" onClick={verifyChain} disabled={verifying} className="h-8" data-testid="ledger-verify-btn">
            {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
            Verify Chain
          </Button>
        </div>
      </div>

      {/* Verify Result */}
      {verifyResult && (
        <div className={`p-3 rounded-lg border text-xs flex items-center gap-2 ${verifyResult.valid ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400" : "border-red-500/30 bg-red-500/5 text-red-400"}`} data-testid="ledger-verify-result">
          {verifyResult.valid ? <ShieldCheck className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
          {verifyResult.valid
            ? `Chain integrity verified: ${verifyResult.blocks_checked} blocks, all SHA-256 links intact. Compliance: ${verifyResult.compliance?.join(", ")}`
            : `Integrity broken at block(s): ${verifyResult.broken_links?.join(", ")}`}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total Blocks", value: stats.total_blocks?.toLocaleString() || "0", icon: Hash, color: "var(--qn-primary)" },
          { label: "Total Savings", value: `$${(stats.total_savings || 0).toFixed(2)}`, icon: Shield, color: "#10b981" },
          { label: "Quantum Blocks", value: stats.quantum_blocks || 0, icon: Clock, color: "#8b5cf6" },
          { label: "Verified", value: `${stats.verified_pct || 0}%`, icon: CheckCircle, color: "#06b6d4" },
        ].map((s) => (
          <div key={s.label} className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)] flex items-center gap-3" data-testid={`ledger-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: s.color + "15" }}>
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
            </div>
            <div>
              <div className="text-[10px] text-[var(--qn-foreground-muted)]">{s.label}</div>
              <div className="text-base font-bold font-mono text-[var(--qn-foreground)]">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-muted)]" />
          <Input placeholder="Search by hash, model..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="ledger-search" />
        </div>
        <Select value={filterDecision} onValueChange={setFilterDecision}>
          <SelectTrigger className="w-44 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="ledger-decision-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Decisions</SelectItem>
            <SelectItem value="model_switch">Model Switch</SelectItem>
            <SelectItem value="cost_optimization">Cost Optimization</SelectItem>
            <SelectItem value="latency_optimization">Latency Opt</SelectItem>
            <SelectItem value="provider_failover">Failover</SelectItem>
            <SelectItem value="quality_upgrade">Quality Upgrade</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSolver} onValueChange={setFilterSolver}>
          <SelectTrigger className="w-32 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="ledger-solver-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Solvers</SelectItem>
            <SelectItem value="quantum">Quantum</SelectItem>
            <SelectItem value="classical">Classical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Block list */}
      {blocks.length === 0 ? (
        <div className="text-center py-12">
          <Link className="w-10 h-10 text-[var(--qn-primary)]/30 mx-auto mb-3" />
          <p className="text-sm text-[var(--qn-foreground-muted)]">No ledger blocks found. Blocks are recorded when optimization decisions are made.</p>
        </div>
      ) : (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] overflow-hidden">
          <CardContent className="p-0 max-h-[400px] overflow-y-auto">
            {blocks.slice(0, 30).map((block, idx) => (
              <motion.div
                key={block.block_number}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.02 }}
                className="flex items-center gap-4 px-4 py-3 border-b border-[var(--qn-border)]/30 hover:bg-[var(--qn-surface)]/20 transition-colors"
                data-testid={`block-${block.block_number}`}
              >
                <div className="w-16 shrink-0">
                  <span className="text-xs font-mono text-[var(--qn-primary)]">#{block.block_number}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className="text-[9px]" style={{ borderColor: (DECISION_COLORS[block.decision] || "#888") + "40", color: DECISION_COLORS[block.decision] || "#888" }}>
                      {(block.decision || "").replace(/_/g, " ")}
                    </Badge>
                    <Badge variant="outline" className="text-[9px]">{block.solver}</Badge>
                    {block.verified && <CheckCircle className="w-3 h-3 text-emerald-500" />}
                  </div>
                  <div className="text-[10px] font-mono text-[var(--qn-foreground-muted)] truncate">
                    {(block.hash || "").slice(0, 20)}...{(block.hash || "").slice(-8)}
                  </div>
                </div>
                <div className="text-xs text-[var(--qn-foreground-muted)] shrink-0">
                  <span className="font-mono">{block.from_model}</span> <ArrowRight className="w-3 h-3 inline" /> <span className="font-mono">{block.to_model}</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-mono text-emerald-500">-${Number(block.savings || 0).toFixed(4)}</div>
                  <div className="text-[10px] text-[var(--qn-foreground-subtle)]">{block.timestamp ? new Date(block.timestamp).toLocaleTimeString() : ""}</div>
                </div>
              </motion.div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
